// Tutor DashboardPage — welcome heading + course cards with roster quick-links.
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.js";
import { useFetch } from "../../hooks/useApi.js";
import Badge from "../../components/shared/Badge.js";
import type { Course, PaginatedResult } from "@dm-lms/shared/types";

export default function DashboardPage() {
  const { user } = useAuth();
  const { data, isLoading, error } = useFetch<PaginatedResult<Course>>("/api/courses?page=1&pageSize=100");

  const myCourses = useMemo(
    () => (data?.items ?? []).filter((course) => course.tutorId === user?.id),
    [data, user]
  );

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Welcome, {user?.name ?? "Tutor"}</h1>

      {isLoading && <p className="text-dm-slate text-sm">Loading your courses…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!isLoading && !error && (
        <>
          <h2 className="text-lg font-medium text-dm-navy mb-3">Your Courses</h2>
          {myCourses.length === 0 ? (
            <p className="text-dm-slate text-sm">No courses assigned to you yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {myCourses.map((course) => (
                <div key={course.id} className="rounded-lg border border-gray-200 bg-white p-4 flex flex-col">
                  <p className="font-medium text-dm-navy mb-2">{course.title}</p>
                  <div className="mb-3">
                    <Badge tone={course.isPublished ? "green" : "gray"}>
                      {course.isPublished ? "Published" : "Draft"}
                    </Badge>
                  </div>
                  <p className="text-sm text-dm-slate mb-4">{course.mode} · {course.capacity} seats</p>
                  <Link
                    to={`/app/tutor/roster?courseId=${course.id}`}
                    className="mt-auto text-sm font-medium text-dm-blue hover:underline"
                  >
                    View roster →
                  </Link>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
